import {
  SET_AUTH_DATA,
  FORCE_CHANGE_PASSWORD,
  MFA_CONFIRMATION_REQUIRED,
  USER_AUTHENTICATED,
  USER_INFO,
  USER_SIGNOUT,
  SETUP_TOTP,
  TOTP_COMPLETE,
} from "./reducer";

import {
  signIn as signInAuth,
  signUp as signUpAuth,
  signOut,
  getCurrentUser as currentAuthenticatedUser,
  confirmSignUp as confirmSignUpAuth,
  resendSignUpCode,
  confirmSignIn,
  updateMFAPreference,
  resetPassword as resetPasswordAuth,
  confirmResetPassword,
  fetchMFAPreference,
  setUpTOTP,
  verifyTOTPSetup,
  updatePassword as updatePasswordAuth,
} from "@aws-amplify/auth";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import i18n from "../../assets/i18n";
import { getRedirectPath } from "@repo/auth-utils/get-redirect-path";
import * as Sentry from "@sentry/react";

import { apiHandler, publicApiHandler } from "../../lib/apiHandler";

//SET user info to redux
export const getCurrentUser = (userData) => async (dispatch) => {
  //check whether user has an active session
  // if yes load it to redux
  try {
    let user_session = await currentAuthenticatedUser({
      bypassCache: true,
    });
    //fetch user profile
    let profile = userData || (await fetchProfileInfo());
    if (profile.is_glu_emp) {
      dispatch({
        type: USER_INFO,
        payload: {
          user: user_session,
          profile,
          full_name: profile?.full_name || "",
          mobile_number: profile?.mobile_number || "",
          email: profile?.email || "",
        },
      });
    } else {
      dispatch(setAuthData("authLoading", false));
    }
  } catch (err) {
    console.error("Error in getCurrentUser:", err);
    dispatch(setAuthData("authLoading", false));
  }
};

const fetchProfileInfo = async () => {
  let reqData = await apiHandler("get", "/user/profile");
  return reqData?.data || "";
};

export const signUp =
  ({ family_name, given_name, phone_number, email, password, navigate }) =>
  async (dispatch) => {
    try {
      let username = uuidv4();
      const { user } = await signUpAuth({
        username,
        password,
        options: {
          userAttributes: {
            email,
            phone_number,
            family_name,
            given_name,
          },
        },
      });

      if (user) {
        dispatch(setAuthData("user", user));
        navigate(
          "/account-verification/signup?uid=" + username + "&mode=register",
        );
      }
    } catch (error) {
      toast.error(error.message || i18n.t("Something went wrong, try again"));
    }
  };

export const setAuthData = (name, value) => (dispatch) => {
  dispatch({
    type: SET_AUTH_DATA,
    payload: { [name]: value },
  });
};

export const confirmSignUp =
  ({ username, code }) =>
  (dispatch) => {
    confirmSignUpAuth({ username, confirmationCode: code })
      .then(() => {
        toast.success(i18n.t("Email verified"));
        dispatch(setAuthData("isEmailVerified", true));
      })
      .catch((error) => {
        toast.error(error.message || i18n.t("Something went wrong, try again"));
      });
  };

export const resendSignUp = (username) => async (_dispatch) => {
  try {
    await resendSignUpCode({ username });
    toast.success(i18n.t("Verification code has been sent to your email"));
  } catch (error) {
    toast.error(error.message || i18n.t("Something went wrong, try again"));
  }
};

export const verifyTokenExpiration = (token) => async (dispatch) => {
  try {
    const reqId = new URLSearchParams(window.location.search).get("req_id");
    const reqIdTrimmed = (reqId ?? "").trim();
    console.info("[InviteFlow] verifyTokenExpiration:start", {
      reqId,
      hasToken: Boolean(token),
      path: window.location.pathname,
    });
    const req_params = { token };
    let resData = await publicApiHandler(
      "post",
      "/user/check-expiration",
      req_params,
    );

    let { status, user } = resData.data ?? {};
    const emailFromInviteUrl = new URLSearchParams(window.location.search).get(
      "email",
    );
    const resolvedEmail =
      (user?.email && String(user.email).trim()) || emailFromInviteUrl || "";
    console.info("[InviteFlow] verifyTokenExpiration:response", {
      reqId,
      code: resData?.code,
      status,
      hasUser: Boolean(user),
      email: resolvedEmail || null,
      usedInviteUrlEmail: Boolean(!user?.email && emailFromInviteUrl),
    });

    if (resData.code != 200 || status == "expired") {
      dispatch(setAuthData("tokenVetificationStatus", "EXPIRED"));
      return;
    }
    if (status == "active" && (resolvedEmail || reqIdTrimmed)) {
      if (resolvedEmail) {
        dispatch(setAuthData("verifyingUserName", resolvedEmail));
      }
      dispatch(setAuthData("tokenVetificationStatus", "VALID"));
      return;
    }
    console.warn("[InviteFlow] Active token missing user email and req_id", {
      reqId,
      status,
    });
    dispatch(setAuthData("tokenVetificationStatus", "EXPIRED"));
  } catch (err) {
    const reqId = new URLSearchParams(window.location.search).get("req_id");
    console.error("[InviteFlow] verifyTokenExpiration:error", { reqId, err });
    dispatch(setAuthData("tokenVetificationStatus", "EXPIRED"));
    toast.error(i18n.t("Token verification failed"));
  }
};

const INVITE_SIGNUP_ERROR_KEY = "cannot_complete_registration";

export const otpVerification =
  ({ username, otp, password, navigate }) =>
  async (dispatch) => {
    const signInUsername = (username ?? "").trim();
    const trimmedOtp = (otp ?? "").trim();
    try {
      dispatch(setAuthData("disable_signIn_button", true));
      const { nextStep } = await signInAuth({
        username: signInUsername,
        password: trimmedOtp,
      });
      if (
        nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        await confirmSignIn({ challengeResponse: password });
      }
      await dispatch(getCurrentUser());
      dispatch(setAuthData("disable_signIn_button", false));
      let userData = await fetchProfileInfo();
      await loginTrack(userData.id, "active"); //first login
      toast.success("Welcome to GLU");
      navigate("/dashboard/profile/edit");
    } catch (error) {
      console.error("[InviteFlow] otpVerification failed", {
        error: error?.name || error?.message || String(error),
      });
      Sentry.captureException(error, {
        tags: { flow: "invite_signup", reason: "otp_or_password_failed" },
      });
      toast.error(i18n.t(INVITE_SIGNUP_ERROR_KEY));
      dispatch(setAuthData("disable_signIn_button", false));
    }
  };

// Helper function to handle new password requirement
const handleNewPasswordRequired = (
  dispatch,
  user,
  username,
  password,
  mfaRetry,
) => {
  dispatch({
    type: FORCE_CHANGE_PASSWORD,
    payload: {
      cognitoUser: user,
      temp_user_info: {
        username: username,
        password: password,
        retry: (mfaRetry || 0) + 1,
      },
    },
  });
};

// Helper function to handle MFA requirement
const handleMfaRequired = (dispatch, user, username, password, mfaRetry) => {
  dispatch({
    type: MFA_CONFIRMATION_REQUIRED,
    payload: {
      cognitoUser: user,
      temp_user_info: {
        username: username,
        password: password,
        retry: (mfaRetry || 0) + 1,
      },
    },
  });
};

// Helper function to check if MFA is required
const isMfaRequired = (user) => {
  return (
    user.challengeName === "MFA_REQUIRED" ||
    user.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
    user.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
  );
};

// Helper function to handle successful authentication
const handleSuccessfulAuth = async (
  dispatch,
  user,
  userData,
  navigate,
  location = null,
) => {
  if (userData.is_glu_emp) {
    await dispatch(getCurrentUser(userData));
    await loginTrack(userData.id);
    await dispatch({ type: USER_AUTHENTICATED, payload: user });
    toast.success("Welcome to GLU");
    const redirectPath = getRedirectPath(location);
    navigate(redirectPath);
  } else {
    toast.error("Unauthorized Access");
    await signOut();
  }
};

// Helper function to format error message
const formatErrorMessage = (error) => {
  const msg = {
    "PreAuthentication failed with error User Disabled.": "User Disabled",
    "PreAuthentication failed with error User Not Found.": "User Not Found",
  };
  let message = msg[error.message] ? msg[error.message] : error.message;
  if (message.includes("PhoneNumber Reason")) {
    message = i18n.t(
      "SMS verification failed with invalid Mobile number. Please Contact admin to change Mobile number.",
    );
  }
  return message || i18n.t("Something went wrong, try again");
};

export const signIn =
  ({ username, password, navigate, mfaRetry, location }) =>
  async (dispatch) => {
    try {
      if (mfaRetry && mfaRetry > 3) {
        toast.error(i18n.t("Maximum retries reached"));
        dispatch(setAuthData("disable_signIn_button", false));
        return;
      }
      dispatch(setAuthData("disable_signIn_button", true));
      const user = await signInAuth({ username, password });

      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        handleNewPasswordRequired(dispatch, user, username, password, mfaRetry);
      } else if (isMfaRequired(user)) {
        handleMfaRequired(dispatch, user, username, password, mfaRetry);
      } else {
        const userData = await fetchProfileInfo();
        await handleSuccessfulAuth(
          dispatch,
          user,
          userData,
          navigate,
          location,
        );
      }
      dispatch(setAuthData("disable_signIn_button", false));
    } catch (error) {
      console.error("Error in signIn:", error);
      const message = formatErrorMessage(error);
      toast.error(message);
      dispatch(setAuthData("disable_signIn_button", false));
    }
  };

export const resendVerificationCode =
  ({ username, password, mfaRetry }) =>
  async (dispatch) => {
    try {
      if (mfaRetry && mfaRetry > 2) {
        toast.error(i18n.t("Maximum retries reached"));
        dispatch(setAuthData("mfa", false));
        return;
      }
      //to be repaced with resend MFA function once it's available
      //https://github.com/aws-amplify/amplify-js/issues/6676
      const user = await signInAuth({ username, password });

      if (
        user?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
        user?.nextStep?.signInStep === "SMS_MFA" ||
        user?.nextStep?.signInStep === "SOFTWARE_TOKEN_MFA"
      ) {
        dispatch({
          type: MFA_CONFIRMATION_REQUIRED,
          payload: {
            cognitoUser: user,
            temp_user_info: {
              username: username,
              password: password,
              retry: (mfaRetry || 0) + 1,
            },
          },
        });
        let destination =
          user?.nextStep?.codeDeliveryDetails?.destination || "your device";
        toast.success(
          `${i18n.t("verification code has been sent to")} ${destination}`,
        );
      }
    } catch (error) {
      console.error("Error in resendVerificationCode:", error);
      toast.error(i18n.t("Maximum retries reached"));
    }
  };

/* Reset password function */
export const requestPasswordReset =
  ({ username, DisableBtn }) =>
  async (dispatch) => {
    try {
      const req_params = {
        email: username,
      };
      let response = await publicApiHandler(
        "post",
        "/user/user-by-email",
        req_params,
      );
      if (response?.data.is_glu_emp) {
        await resetPasswordAuth({ username });
        dispatch(setAuthData("isResetMailSend", true));
      } else {
        DisableBtn(false);
        toast.error(i18n.t("User not found"));
      }
    } catch (error) {
      toast.error(error.message || i18n.t("Something went wrong, try again"));
    }
  };

export const resetPassword =
  ({ username, code, password }) =>
  async (dispatch) => {
    try {
      await confirmResetPassword({
        username,
        newPassword: password,
        confirmationCode: code,
      });
      dispatch(setAuthData("passwordResetComplete", true));
    } catch (error) {
      console.error("Error in resetPassword:", error);
      dispatch(setAuthData("passwordResetLinkExpired", true));
      const errorMessage = error?.message || i18n.t("Link Expired");
      toast.error(errorMessage);
    }
  };

export const confirmLogin =
  ({ code, navigate, passwordAttempts, location }) =>
  async (dispatch) => {
    try {
      dispatch(setAuthData("disable_signIn_button", true));
      dispatch(setAuthData("passwordAttempts", passwordAttempts + 1));
      let user = await confirmSignIn({ challengeResponse: code });
      // user authenticated
      dispatch({ type: USER_AUTHENTICATED, payload: user });
      //set user session to redux
      dispatch(getCurrentUser());
      let userData = await fetchProfileInfo();
      await loginTrack(userData.id);
      dispatch(setAuthData("disable_signIn_button", false));
      toast.success(i18n.t("Welcome to GLU"));
      const redirectPath = getRedirectPath(location);
      navigate(redirectPath);
    } catch (err) {
      console.error("Error in confirmLogin:", err);
      dispatch(setAuthData("disable_signIn_button", false));
      if (passwordAttempts > 2) {
        toast.error(i18n.t("Maximum retries reached please login again"));
        dispatch(setAuthData("mfa", false));
        dispatch(setAuthData("passwordAttempts", 0));
        return;
      }
      const errorMessage =
        err?.message || i18n.t("Incorrect password please try again");
      toast.error(errorMessage);
    }
  };

export const setNewPassword =
  ({ password, navigate, location }) =>
  async (dispatch) => {
    try {
      let user = await confirmSignIn({ challengeResponse: password });
      // inspect response and check whether
      // 1. MFA confirmation is required, dispatch->MFA_CONFIRMATION_REQUIRED
      // 2. otherwise, authenticate user, dispatch -> USER_AUTHENTICATED
      if (
        user.challengeName === "SMS_MFA" ||
        user.challengeName === "SOFTWARE_TOKEN_MFA"
      ) {
        dispatch({
          type: MFA_CONFIRMATION_REQUIRED,
          payload: {
            cognitoUser: { ...user },
            temp_user_info: { username: user.username, password },
          },
        });
      } else {
        // user authenticated
        dispatch({ type: USER_AUTHENTICATED, payload: user });
        dispatch(getCurrentUser());
        let userData = await fetchProfileInfo();
        await loginTrack(userData.id);
        toast.success(i18n.t("Welcome to GLU"));
        const redirectPath = getRedirectPath(location);
        navigate(redirectPath);
      }
    } catch (err) {
      console.error("Error in setNewPassword:", err);
      const errorMessage =
        err?.message || i18n.t("Something went wrong, try again");
      toast.error(errorMessage);
    }
  };

export const logout = (navigate) => async (dispatch) => {
  try {
    await signOut();
    dispatch({ type: USER_SIGNOUT });
    navigate("/");
  } catch (err) {
    console.error("signOut-error", err);
    toast.error(err.message || i18n.t("Something went wrong, try again"));
  }
};

export const getPreferredMFA = () => async (dispatch) => {
  try {
    let mfaSettings = await fetchMFAPreference();
    let smsValue = "NOMFA";
    if (mfaSettings?.enabled?.includes("TOTP")) {
      smsValue = "TOTP";
    } else if (mfaSettings?.enabled?.includes("SMS")) {
      smsValue = "SMS";
    }
    dispatch(setAuthData("mfa_mode", smsValue));
  } catch (err) {
    console.error("error", err);
  }
};

/* To enable the TOTP MFA  */
export const setupTOTP = (user) => async (dispatch) => {
  try {
    // To setup TOTP, first you need to get a `authorization code` from Amazon Cognito
    // `user` is the current Authenticated user
    let code = await setUpTOTP();
    // You can directly display the `code` to the user or convert it to a QR code to be scanned.
    // E.g., use following code sample to render a QR code with `qrcode.react` component:
    //
    const totp_connection_string =
      "otpauth://totp/AWSCognito:" +
      user.username +
      "?secret=" +
      code.sharedSecret +
      "&issuer=" +
      "GLU.CX";
    dispatch({ type: SETUP_TOTP, payload: totp_connection_string });
  } catch (err) {
    toast.error(err.message || i18n.t("Something went wrong, try again"));
  }
};

export const verifyTotpToken =
  ({ user, code }) =>
  async (dispatch) => {
    try {
      await verifyTOTPSetup({ code });
      // don't forget to set TOTP as the preferred MFA method
      await updateMFAPreference({ totp: "PREFERRED" });
      toast.success(i18n.t("TOTP MFA enabled successfully"));
      dispatch({ type: TOTP_COMPLETE });
      dispatch(getPreferredMFA());
    } catch (err) {
      console.error("Error in verifyTotpToken:", err);
      const errorMessage =
        err?.message ||
        i18n.t("Code mismatch and failed to enable software token MFA");
      toast.error(errorMessage);
    }
  };

/* To disable the SMS MFA and TOTP MFA */
export const disableMfa =
  (currentMfa = "") =>
  async (dispatch) => {
    try {
      if (currentMfa === "TOTP") {
        await updateMFAPreference({ totp: "DISABLED" });
      }
      if (currentMfa === "SMS") {
        await updateMFAPreference({ sms: "DISABLED" });
      }
      toast.success(currentMfa + i18n.t(" MFA disabled successfully"));
      dispatch(getPreferredMFA());
    } catch (err) {
      toast.error(err.message || i18n.t("Something went wrong, try again"));
    }
  };

export const setupSmsMfa = () => async (dispatch) => {
  try {
    await updateMFAPreference({ sms: "ENABLED" });
    toast.success(i18n.t("SMS MFA enabled successfully"));
    dispatch(getPreferredMFA());
  } catch (err) {
    toast.error(err.message || i18n.t("Something went wrong, try again"));
  }
};

export const verifyResetLink = (token) => async (dispatch) => {
  try {
    const req_params = { token };
    let response = await publicApiHandler(
      "post",
      "/user/token_reset",
      req_params,
    );
    let token_details = response?.data;
    if (token_details?.cognito_id) {
      dispatch(setAuthData("passwordResetLinkValidation", false));
    } else {
      dispatch(setAuthData("passwordResetLinkExpired", true));
    }
  } catch (err) {
    console.error("Error in verifyResetLink:", err);
    dispatch(setAuthData("passwordResetLinkExpired", true));
    toast.error(i18n.t("Failed to verify reset link"));
  }
};

export const updateProfile =
  ({ full_name, email, mobile_number }) =>
  async (dispatch) => {
    try {
      dispatch(setAuthData("profileUpdateBtnDisable", true));
      const req_params = {
        full_name,
        email,
        mobile_number,
      };

      await apiHandler("put", "/user/profile/update", req_params);

      toast.success(i18n.t("Profile updated successfully"));
      dispatch(getCurrentUser());
      dispatch(setAuthData("profileUpdateBtnDisable", false));
    } catch (err) {
      let errMessage =
        err?.message || i18n.t("Something went wrong, try again");
      toast.error(errMessage);
      dispatch(setAuthData("profileUpdateBtnDisable", false));
    }
  };

export const updatePassword =
  ({ oldPassword, newPassword }) =>
  async (dispatch) => {
    try {
      dispatch(setAuthData("passwordUpdateBtnDisable", true));
      await updatePasswordAuth({ oldPassword, newPassword });
      toast.success(i18n.t("Password updated successfully"));
      dispatch({
        type: SET_AUTH_DATA,
        payload: {
          password: "",
          oldPassword: "",
          newPassword: "",
          passwordUpdateBtnDisable: false,
        },
      });
    } catch (err) {
      let errMessage =
        err?.message || i18n.t("Something went wrong, try again");
      toast.error(errMessage);
      dispatch(setAuthData("passwordUpdateBtnDisable", false));
    }
  };

const loginTrack = async (id, status) => {
  try {
    const req_body = { id };
    if (status) {
      req_body["status"] = status;
    }
    await apiHandler("post", "/user/login-activity-track", req_body);
    return true;
  } catch (err) {
    console.error("err", err);
    throw err;
  }
};

export const SendLogin = () => async () => {
  try {
    let resData = await apiHandler("get", "/user/addloginactivity");
    return resData;
  } catch (err) {
    console.error(err);
  }
};
